import Vue from 'vue'
import { validationMixin } from 'vuelidate'
import {maxLength, required, requiredIf} from 'vuelidate/lib/validators'
import ErrorHandler from 'utils/ErrorHandler'
import conf from 'conf'
import axios from 'axios'
import every from 'lodash-es/every'
import flatMap from 'lodash-es/flatMap'
import cloneDeep from 'lodash-es/cloneDeep'

const formErrors = {
  files: {
    maxLength: 'reusable.fileUpload.validation.maxLength',
    required: 'reusable.fileUpload.validation.required',
    fileSize: 'claims.files.validation.fileSize',
    fileFormat: 'claims.files.validation.fileFormat',
    totalFileSize: 'reusable.fileUpload.validation.totalFileSize',
    fileCategory: 'reusable.fileUpload.validation.fileCategory',
  }
}

const ERRORS = {
  WRONG_FILE_EXTENSION: 'generic.errors.wrongFileExtension',
  WRONG_OR_EXPIRED_FILE_UPLOAD_TOKEN: 'generic.errors.wrongOrExpiredUploadToken',
  REQUEST_ENTITY_TOO_LARGE: 'generic.errors.requestEntityTooLarge'
}

const defaultState = {
  files: [],
  oldFiles: [],
  formErrors: formErrors,
  token: '',
  submitBtnDisabled: false,
  isRequired: false,
  maxLength: 3,
  totalFileSize: 0,
  checkEachFileSize: true,
  withReimbursementType: false
}

const validator = new Vue({
  mixins: [validationMixin],
  computed: {
    state() {
      return defaultState
    }
  },
  validations() {
    const data = {
      files: {
        maxLength: maxLength(this.state.maxLength),
        required,
        $each: {
          file: {
            fileSize: (value) => {
              if (!this.state.checkEachFileSize) {
                return true
              }
              return (value.size / 1000 / 1000) <= 5
            },
            fileFormat: (value) => {
              let format = value.name.split('.').slice(-1)[0]?.toLowerCase()
              return ['jpg', 'jpeg', 'png', 'pdf', 'doc', 'docx', 'bmp', 'edoc', 'asice', 'bdoc'].includes(format)
            },
          },
          fileCategory: {
            required: requiredIf(
              () => !!this.state.withReimbursementType
            )
          }
        },
        totalFileSize: (value, state) => {
          let total = 0
          state.files.forEach(file => {
            total += file.file.size
          })

          return (total / 1000 / 1000) <= 15
        }
      }
    }
    if (!this.state.isRequired) {
      delete data.files.required
    }
    return { state: data }
  }
})

const FilesForm = {
  state: defaultState,
  mutations: {
    disableBtn(state, value) {
      state.submitBtnDisabled = value
    },
    addFiles(state, newFiles) {
      newFiles.forEach(file => {
        state.files = state.files.concat({
          file: file,
          fileCategory: ''
        })
      })
    },
    addCategory(state, { selected, index }) {
      const file = state.files[index]
      if (!file) {
        return
      }
      file.fileCategory = selected
      state.files[index]=file
    },
    updateCategory(state, { selected, index }) {
      const file = state.files[index]
      if (file) {
        file.fileCategory = selected
        state.files[index] = file
      }
    },
    removeFile(state, index) {
      state.files.splice(index, 1)
    },
    removeFailedFile(state, index) {
      state.oldFiles.splice(index, 1)
      const statuses = flatMap(state.oldFiles, 'status')
      state.submitBtnDisabled = !every(statuses, (o) => o === 'success')
    },
    removeAllFiles(state) {
      state.files = []
    },
    addOldFile(state, value) {
      state.oldFiles = state.oldFiles.concat(value)
    },
    setOldFiles(state, value) {
      state.oldFiles = value
    },
    setToken(state, value) {
      state.token = value
    },
    setIsRequired(state, value) {
      state.isRequired = value
    },
    setMaxLength(state, value) {
      state.maxLength = value
    },
    setTotalFileSize(state, value) {
      state.totalFileSize = value
    },
    setCheckEachFileSize(state, value) {
      state.checkEachFileSize = value
    },
    setRequiredMessage(state, value) {
      state.formErrors.files.required = value
    },
    setWithReimbursementType(state, value) {
      state.withReimbursementType = value
    },
    updateAllFiles(state, value) {
      state.files = value
    }
  },

  actions: {
    submitForm({ state, dispatch, commit }, additionalFiles = []) {
      return new Promise((resolve) => {
        commit('disableBtn', true)
        dispatch('touchEverything')
        const vState = validator.$v.state
        const hasError = flatMap(state.oldFiles, 'status')
        if (!vState.$invalid) {
          const parsedFiles = state.oldFiles.length
          dispatch('sendFiles', additionalFiles).then((data) => {
            if (
              Number(parsedFiles + data.parsedFiles) ===
              state.files.length + state.oldFiles.length
            ) {
              const mergedData = hasError.concat(data.hasError)
              const allValid = every(mergedData, (o) => o === 'success')
              commit('disableBtn', !allValid)

              return resolve(allValid)
            }
          })
        }
      })
    },
    clearForm({ commit }) {
      commit('setToken', '')
      commit('setOldFiles', [])
      commit('removeAllFiles')
    },
    touchFiles({ state, commit }) {
      const vState = validator.$v.state
      vState.$touch()

      state.files.forEach((file, index) => {
        let fValidation = vState.files.$each[index]
        if (fValidation.$error) {
          file.status = 'error'
          let errorMsg = ''
          if (!fValidation.file.fileSize) {
            errorMsg = 'fileSize'
          }
          if (!fValidation.file.fileFormat) {
            errorMsg = 'fileFormat'
          }
          if (!fValidation.fileCategory.required) {
            errorMsg = 'fileCategory'
          }
          file.errorMsg = formErrors.files[errorMsg]
        } else {
          file.status = 'success'
        }
      })
      commit('updateAllFiles', state.files)
    },

    touchEverything() {
      const vState = validator.$v.state
      vState.$touch()
    },

    touch() {
      return new Promise((resolve, reject) => {
        let vState = validator.$v.state
        vState.$touch()

        !vState.$invalid ? resolve(true) : reject('attachment-claims')
      })
    },

    updateCategory({ state, commit, dispatch }, { selected, index }) {
      commit('updateCategory', { selected, index })
      dispatch('touchFiles')
    },
    sendFiles({ state, commit, dispatch }, additionalFiles = []) {
      const response = []
      let parsedFiles = 0
      const hasError = []
      let totalFiles = state.files.concat(additionalFiles)

      totalFiles.forEach((file) => {
        const action = dispatch('sendFile', file)
          .then(() => (file.status = 'success'))
          .catch((error) => {
            file.status = 'error'
            file.errorMsg = ErrorHandler.getErrorTranslationKey(ERRORS, error.response)
          })
          .then(() => {
            commit('addOldFile', file)
            commit('removeFile', state.files.indexOf(file))
            hasError.push(file.status)
            parsedFiles++
          })

        response.push(action)
      })

      return Promise.all(response)
        .then(() => {
          return Promise.resolve({
            parsedFiles,
            hasError
          })
        })
        .catch(() => {
          return Promise.resolve({
            parsedFiles,
            hasError
          })
        })
    },
    sendFile({ state }, file) {
      const formData = new FormData()
      let fileToSubmit = file
      if (file.file) {
        fileToSubmit = file.file
      }
      formData.append('file', fileToSubmit)

      if (file?.fileCategory) {
        formData.append('claimCategory', file.fileCategory)
      }

      return axios.post(conf.btApiUrl + '/contact/file', formData, {
        headers: {
          'file-upload-token': state.token,
          'Content-Type': 'multipart/form-data'
        }
      })
    }
  },

  getters: {
    validation() {
      return Object.assign({}, validator.$v.state)
    }
  },

  namespaced: true
}

export default FilesForm
